import React, {createContext, useCallback, useContext, useEffect, useState} from "react";
import {useCounter} from "../../../Utilities/Reducer";
import {emptyFunction, returnFalse, returnTrue} from "../../../Utilities/Api/callbackUtilities";
import {districtsApi} from "../../../SiteConfig";
import { siteApi } from "../../../SiteConfig/siteApi";

import type {HandleApiCall, HandleApiCallWithNoDim, ApiCall, ApiCallback} from "../../../types";
import type {District, AphQuotaDates} from "../../../types";

export interface AppContextValue {
    isLoading: boolean;
    isLoadingCss?: boolean;
    handleApiCall: HandleApiCall;
    handleApiCallWithNoDim: HandleApiCallWithNoDim;
    returnUrl: string | undefined;
    setReturnUrl: (url: string | undefined) => void;
    districts: District[];
    appMode: string;
    aphQuotaDates: AphQuotaDates;
    [key: string]: unknown;
}

export const AppContext = createContext<AppContextValue>({} as AppContextValue);
export const useAppContext = (): AppContextValue => useContext(AppContext);

interface AppContextProviderProps {
    children: React.ReactNode;
}

export const AppContextProvider: React.FC<AppContextProviderProps> = ({children}) => {
const calls = useCounter();
const [isLoading, setIsLoading] = useState(false);
const [returnUrl, setReturnUrl] = useState<string | undefined>(undefined);
const [districts, setDistricts] = useState<District[]>([]);
const [appMode, setAppMode] = useState("");
const [aphQuotaDates, setAphQuotaDates] = useState<AphQuotaDates>({ isAphQuotaWindow: false, fiscalYearStartDate: '', fiscalYearEndDate: '', aphEndDate: '' });

const handleApiCall: HandleApiCall = useCallback((apiCall: ApiCall, thenCallback: ApiCallback = returnTrue, catchCallback: ApiCallback = returnFalse, useCounter = true) => {
        if (useCounter)
            calls.increment();

        return new Promise((resolve) => {
            const resolveCall = (result: unknown) => {
                if (useCounter)
                    calls.decrement();

                resolve(thenCallback(result));
            };
            const rejectCall = (error: unknown) => {
                if (useCounter)
                    calls.decrement();

                resolve(catchCallback(error));
            };

            apiCall()
                .then(
                    (result: unknown) => resolveCall(result),
                    (error: unknown) => rejectCall(error),
                )
                .catch((error: unknown) => rejectCall(error));
        });
    }, [calls]);

    const handleApiCallWithNoDim: HandleApiCallWithNoDim = (apiCall: ApiCall, thenCallback: ApiCallback = emptyFunction, catchCallback: ApiCallback = emptyFunction) => {
        return handleApiCall(apiCall, thenCallback, catchCallback, false);
    };

    useEffect(() => {
        if (isLoading && calls.count === 0)
            setIsLoading(false);
        if (!isLoading && calls.count > 0)
            setIsLoading(true);
        if (calls.count < 0)
            calls.reset();
    }, [calls, calls.count, isLoading]);

    useEffect(() => {
        const fetchDistricts = () => {
            return handleApiCall(districtsApi.getDistricts, setDistricts);
        };

        const fetchAppMode = () => {
            return handleApiCall(
                siteApi.getEnvironment,
                (result: unknown) => {
                    setAppMode((result as { environment?: string })?.environment || "");
                },
                () => {
                    setAppMode("");
                }
            );
        };

        const fetchIsAphQuotaWindow = () => {
            return handleApiCall(
                siteApi.getAphQuotaWindow,
                (result: unknown) => {
                    setAphQuotaDates((result as AphQuotaDates) || { isAphQuotaWindow: false, fiscalYearStartDate: '', fiscalYearEndDate: '', aphEndDate: '' });
                },
                () => {
                    setAphQuotaDates({
                        isAphQuotaWindow: false,
                        fiscalYearStartDate: "",
                        fiscalYearEndDate: "",
                        aphEndDate: ""
                    });
                }
            );
        };

        fetchAppMode().then(() => {
        });

        fetchDistricts().then(() => {
        });

        fetchIsAphQuotaWindow().then(() => {
        });

        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <AppContext
            value={{
                isLoading,
                handleApiCall,
                handleApiCallWithNoDim,
                returnUrl,
                setReturnUrl,
                districts,
                appMode,
                aphQuotaDates,
            }}
        >
            {children}
        </AppContext>
    );
};
